import { useEffect, useState } from 'react';
import { useDependencyManagerContext } from 'src/contexts/dependencyManager';
import { mountDevicePageUrl, mountMethoneCacheApiUrl } from './utils/mountUrls';
import { CommonsNorthboundAPIsType } from 'src/contexts/dependencyManager/utils/getCommonsNorthboundAPIs/types';
import { URLS } from 'src/utils/urls';
import { createLogger, PreffixLogs } from '@uma/react-commons/dist/index';

interface IUseCacheApi {
  serialNumber: string;
  productNumber: string;
  useMethoneCacheApiDeviceSupport: boolean;
}

interface IUseCacheApiState {
  deviceSupportUrl: string;
  isLoading: boolean;
  error: Error | null;
}

const fetchDeviceSupportUrl = async (
  serialNumber: string,
  productNumber: string,
  northboundAPIs: CommonsNorthboundAPIsType
): Promise<string> => {
  const logger = createLogger({
    preffix: PreffixLogs.DEVICES,
    context: 'fetchDeviceSupportUrl'
  });
  try {
    const userToken = await northboundAPIs.v1.authProvider.getAccessToken();
    const methoneCacheApiUrl = mountMethoneCacheApiUrl();

    const response = await fetch(methoneCacheApiUrl, {
      method: 'POST',
      body: JSON.stringify({
        serialNumber: serialNumber,
        productNumber: productNumber
      }),
      headers: {
        Authorization: `Bearer ${userToken}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();

    if (data.CacheToken) {
      return mountDevicePageUrl({
        token: data.CacheToken,
        northboundAPIs: northboundAPIs
      });
    } else {
      logger.warn(
        'Methone Cache API did not return a valid token, using fallback URL'
      );
      return URLS.DeviceSupport;
    }
  } catch (error) {
    logger.warn('Methone Cache API failed, using fallback URL:', error);
    return URLS.DeviceSupport;
  }
};

const useCacheApi = ({
  serialNumber,
  productNumber,
  useMethoneCacheApiDeviceSupport = false
}: IUseCacheApi): IUseCacheApiState => {
  const { northboundAPIs } = useDependencyManagerContext();
  const [deviceSupportUrl, setDeviceSupportUrl] = useState<string>(
    !useMethoneCacheApiDeviceSupport
      ? URLS.DeviceSupport
      : mountDevicePageUrl({
          northboundAPIs: northboundAPIs
        })
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!useMethoneCacheApiDeviceSupport || !serialNumber || !productNumber) {
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const url = await fetchDeviceSupportUrl(
          serialNumber,
          productNumber,
          northboundAPIs
        );
        setDeviceSupportUrl(url);
      } catch (error) {
        setError(error as Error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [
    northboundAPIs,
    productNumber,
    serialNumber,
    useMethoneCacheApiDeviceSupport
  ]);

  return { deviceSupportUrl, isLoading, error };
};

export default useCacheApi;
